import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { generateLandmarkSampleExcel, parseLandmarkExcel } from '../../utils/excelTemplate'


interface City {
    _id: string;
    name: string;
}

interface NearbyLoc {
    name: string;
    km: string;
    image: string;
    category?: string;
}

interface DetailPair {
    label: string;
    value: string;
    icon?: string;
}

interface Locality {
    _id: string;
    name: string;
    city: City | string | null;
    image?: string;
    images?: string[];
    nearbyLocations?: NearbyLoc[];
    description?: string;
    additionalDetails?: DetailPair[];
    isActive?: boolean;
    isFeatured?: boolean;
    createdAt?: string;
}

interface LandmarkCategory {
    _id: string;
    name: string;
}

export default function AdminEditLocality() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { token } = useAuth()
    
    const [cities, setCities] = useState<City[]>([])
    const [categories, setCategories] = useState<LandmarkCategory[]>([])
    const [globalHighlights, setGlobalHighlights] = useState<{ _id: string; name: string; icon: string; description?: string }[]>([])

    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [uploading, setUploading] = useState(false)

    // Form State
    const [formCity, setFormCity] = useState('')
    const [formName, setFormName] = useState('')
    const [formImage, setFormImage] = useState('')
    const [formGallery, setFormGallery] = useState<string[]>([])
    const [formNearby, setFormNearby] = useState<NearbyLoc[]>([])
    const [formDesc, setFormDesc] = useState('')
    const [formAddDetails, setFormAddDetails] = useState<DetailPair[]>([])
    const [formIsFeatured, setFormIsFeatured] = useState(false)

    const handleLandmarkSampleDownload = () => {
        generateLandmarkSampleExcel();
    }

    const handleLandmarkImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const data = await parseLandmarkExcel(file);
            if (data.length === 0) {
                toast.error("Invalid file");
                return;
            }

            const newLandmarks = [...formNearby];
            for (const row of data) {
                if (!row.name || !row.categoryName) continue;
                const cleanCategoryName = row.categoryName.trim();
                let cat = categories.find(c => c.name.toLowerCase() === cleanCategoryName.toLowerCase());
                
                if (!cat) {
                    try {
                        const createCatRes = await fetch(`${API_BASE}/landmark-categories`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({ name: cleanCategoryName, icon: 'fa-location-dot' })
                        });
                        if (createCatRes.ok) {
                            const newCat = await createCatRes.json();
                            setCategories(prev => [...prev, newCat]);
                            cat = newCat;
                        }
                    } catch (err) {
                        console.error('Failed to create new category', err);
                    }
                }
                
                if (cat) {
                    newLandmarks.push({
                        name: row.name.trim(),
                        km: row.km?.toString() || '0.0',
                        image: '',
                        category: cat._id
                    });
                }
            }

            setFormNearby(newLandmarks);
            toast.success("Landmarks imported successfully");
        } catch (err: any) {
            toast.error(err.message || "Error during import");
        } finally {
            e.target.value = '';
        }
    }

    useEffect(() => {

        const fetchInitialData = async () => {
            setLoading(true)
            try {
                // Fetch Cities
                const cityRes = await fetch(`${API_BASE}/cities`)
                const cityData = await cityRes.json()
                if (Array.isArray(cityData)) setCities(cityData.filter(Boolean))

                // Fetch Categories
                const catRes = await fetch(`${API_BASE}/landmark-categories`)
                const catData = await catRes.json()
                if (Array.isArray(catData)) setCategories(catData)

                // Fetch Global Highlights
                const highlightRes = await fetch(`${API_BASE}/locality-highlights`)
                const highlightData = await highlightRes.json()
                if (Array.isArray(highlightData)) setGlobalHighlights(highlightData)

                // Fetch Locality Details ONLY if ID exists
                if (id) {
                    const locRes = await fetch(`${API_BASE}/localities/${id}`)
                    if (!locRes.ok) throw new Error('Locality not found')
                    const loc: Locality = await locRes.json()

                    // Populate Form
                    setFormCity(loc.city && typeof loc.city === 'object' ? loc.city._id : typeof loc.city === 'string' ? loc.city : '')
                    setFormName(loc.name || '')
                    setFormImage(loc.image || '')
                    setFormGallery(loc.images || [])
                    setFormNearby((loc.nearbyLocations || []).map(nb => ({
                        ...nb,
                        category: (nb.category && typeof nb.category === 'object') ? (nb.category as any)._id : nb.category
                    })))
                    setFormDesc(loc.description || '')
                    setFormAddDetails(loc.additionalDetails || [])
                    setFormIsFeatured(loc.isFeatured || false)
                }

            } catch (err) {
                console.error(err)
                toast.error('Failed to load locality details')
                navigate('/admin-localities')
            } finally {
                setLoading(false)
            }
        }

        fetchInitialData()
    }, [id, navigate])

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, target: 'single' | 'gallery' | 'nearby', index?: number) => {
        const files = e.target.files
        if (!files || files.length === 0) return
        setUploading(true)
        try {
            if (target === 'gallery') {
                const uploadedUrls: string[] = [...formGallery]
                for (let i = 0; i < files.length; i++) {
                    const fd = new FormData()
                    fd.append('image', files[i])
                    const res = await fetch(`${API_BASE}/upload/nearby-image`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                        body: fd
                    })
                    const data = await res.json()
                    if (data.url) uploadedUrls.push(data.url)
                }
                setFormGallery(uploadedUrls)
            } else {
                const fd = new FormData()
                fd.append('image', files[0])
                const res = await fetch(`${API_BASE}/upload/nearby-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd
                })
                const data = await res.json()
                if (data.url) {
                    if (target === 'single') setFormImage(data.url)
                    if (target === 'nearby' && typeof index === 'number') {
                        const newNearby = [...formNearby]
                        if (newNearby[index]) {
                            newNearby[index].image = data.url
                            setFormNearby(newNearby)
                        }
                    }
                }
            }
        } catch (err) {
            console.error(err)
            toast.error('Upload failed')
        } finally {
            setUploading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formCity) return toast.error('Regional hub selection required')
        if (!formName.trim()) return toast.error('Sector designation required')

        setSubmitting(true)
        try {
            const url = id ? `${API_BASE}/localities/${id}` : `${API_BASE}/localities`
            const method = id ? 'PUT' : 'POST'
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    city: formCity,
                    name: formName.trim(),
                    image: formImage,
                    images: formGallery,
                    nearbyLocations: formNearby,
                    description: formDesc,
                    additionalDetails: formAddDetails,
                    isFeatured: formIsFeatured
                })
            })

            if (res.ok) {
                toast.success(id ? 'Sector coordinates synchronized.' : 'New sector localized successfuly.')
                navigate('/admin-localities')
            } else {
                const data = await res.json()
                toast.error(data.message || 'Operation failed')
            }
        } catch {
            toast.error('Linkage failure detected')
        }
        setSubmitting(false)
    }

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link'],
            ['clean'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
        ],
    };

    if (loading) return (
        <AdminLayout>
            <div className="flex flex-col items-center justify-center py-32">
                <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-[#05144b] font-bold uppercase tracking-widest text-[12px]">Scanning Frequencies...</p>
            </div>
        </AdminLayout>
    )

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div className="flex items-center gap-6">
                    <button 
                        onClick={() => navigate('/admin-localities')}
                        className="w-12 h-12 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] flex items-center justify-center shadow-sm transition-all active:scale-95 hover:bg-blue-50"
                        title="Back to Registry"
                    >
                        <i className="fa-solid fa-arrow-left"></i>
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-black mb-1 capitalize tracking-tight">{id ? 'Modify sector' : 'Initialize New sector'}</h1>
                        <p className="text-black text-base">Fine-tuning geographical node: <span className="text-black font-bold">{formName || 'New Node'}</span></p>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden mb-12 animate-in fade-in zoom-in-95 duration-500">
                <div className="p-8 border-b border-gray-200 flex items-center justify-between bg-white/50">
                    <div className="flex items-center gap-4">
                        <div>
                        <h5 className="text-xl font-bold text-black capitalize">Sector Configuration profile</h5>
                        <p className="text-[14px] text-black font-medium mt-0.5">Linkage & Metadata Override</p>
                    </div>
                    </div>
                </div>

                <div className="p-8 md:p-12">
                    <form onSubmit={handleSubmit} className="space-y-12">
                        {/* Primary Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="text-[15px] font-bold text-black ml-1">Sector Designation</label>
                                <input
                                    type="text"
                                    className="block w-full px-6 py-4 bg-white border border-gray-300 focus:border-[#05144b] rounded-md text-base text-black font-medium transition-all outline-none shadow-sm"
                                    value={formName}
                                    onChange={(e) => setFormName(e.target.value)}
                                    placeholder="Enter identifier..."
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[15px] font-bold text-black ml-1">Regional Parent Hub</label>
                                <div className="relative">
                                    <select
                                        className="block w-full px-6 py-4 bg-white border border-gray-300 focus:border-[#05144b] rounded-md text-base text-black font-medium transition-all outline-none appearance-none cursor-pointer shadow-sm"
                                        value={formCity}
                                        onChange={(e) => setFormCity(e.target.value)}
                                        required
                                    >
                                        <option value="">Select Hub</option>
                                        {cities.map((city) => (
                                            <option key={city._id} value={city._id}>{city.name}</option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                                        <i className="fa-solid fa-chevron-down text-[12px] text-black"></i>
                                    </div>
                                </div>
                            </div>
                            <div className="md:col-span-2 flex items-center gap-3 bg-gray-50/50 p-4 rounded-md border border-gray-100 mt-2 shadow-sm">
                                <input
                                    type="checkbox"
                                    id="featured-toggle"
                                    className="w-5 h-5 rounded border-gray-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                    checked={formIsFeatured}
                                    onChange={(e) => setFormIsFeatured(e.target.checked)}
                                />
                                <label htmlFor="featured-toggle" className="text-[15px] font-bold text-black cursor-pointer select-none">
                                    <i className="fa-solid fa-star text-yellow-500 mr-2"></i>
                                    Feature on Homepage (Explore by Location Section)
                                </label>
                            </div>
                            <div className="md:col-span-2 space-y-2">
                                <label className="text-[15px] font-bold text-black ml-1">Sector Narrative</label>
                                <div className="admin-quill-wrapper bg-white border border-gray-300 rounded-md overflow-hidden focus-within:border-[#05144b] transition-all shadow-sm">
                                    <ReactQuill
                                        theme="snow"
                                        value={formDesc}
                                        onChange={setFormDesc}
                                        modules={quillModules}
                                        placeholder="Atmosphere, safety, connectivity, and premium features of this sector..."
                                        className="min-h-[250px] border-none"
                                    />
                                </div>
                            </div>    <style>{`
                                    .admin-quill-wrapper .ql-toolbar.ql-snow {
                                        border: none !important;
                                        background: #f8fafc !important;
                                        border-bottom: 1px solid #f1f5f9 !important;
                                    }
                                    .admin-quill-wrapper .ql-container.ql-snow {
                                        border: none !important;
                                        font-size: 1rem !important;
                                        font-family: inherit !important;
                                    }
                                    .admin-quill-wrapper .ql-editor {
                                        min-height: 250px !important;
                                        color: #000000 !important;
                                        font-weight: 400 !important;
                                    }
                                `}</style>
                            </div>
                        
                        {/* Visual Assets */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-gray-100">
                            <div className="space-y-4">
                                <h6 className="text-[15px] font-bold text-black flex items-center gap-2">
                                    <div className="w-2 h-4 bg-[#05144b] rounded-full"></div>
                                    Primary Identification Asset
                                </h6>
                                <div className="relative group aspect-video rounded-md overflow-hidden bg-white flex flex-col items-center justify-center border border-dashed border-gray-200 transition-all hover:border-[#05144b] hover:bg-blue-50 cursor-pointer shadow-inner">
                                    {formImage ? (
                                        <>
                                            <img src={getImageUrl(formImage)} alt="preview" className="w-full h-full object-cover" />
                                            <button 
                                                type="button" 
                                                className="absolute top-4 right-4 w-10 h-10 bg-[#05144b] text-white rounded-full flex items-center justify-center shadow-lg active:scale-90"
                                                onClick={(e) => { e.stopPropagation(); setFormImage(''); }}
                                            >
                                                <i className="fa-solid fa-xmark"></i>
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="w-12 h-12 bg-white rounded-md flex items-center justify-center shadow-md mb-4 text-[#05144b] border border-gray-100">
                                                <i className="fa-solid fa-cloud-arrow-up"></i>
                                            </div>
                                            <div className="text-[12px] font-bold text-[#05144b] uppercase tracking-widest">Transmit Hero Image</div>
                                            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleFileUpload(e, 'single')} />
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h6 className="text-[15px] font-bold text-black flex items-center gap-2">
                                    <div className="w-2 h-4 bg-[#05144b] rounded-full"></div>
                                    Asset Gallery
                                </h6>
                                <div className="min-h-[160px] p-6 bg-white rounded-md border border-gray-100 shadow-inner">
                                    <div className="flex flex-wrap gap-3">
                                        {formGallery.map((img, idx) => (
                                            <div key={idx} className="relative w-16 h-16 rounded-md overflow-hidden shadow-md border-2 border-white">
                                                <img src={getImageUrl(img)} className="w-full h-full object-cover" alt="gal" />
                                                <button type="button" className="absolute inset-0 bg-[#05144b]/80 text-white flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" onClick={() => setFormGallery(formGallery.filter((_, i) => i !== idx))}>
                                                    <i className="fa-solid fa-trash-can text-[10px]"></i>
                                                </button>
                                            </div>
                                        ))}
                                        <div className="relative w-16 h-16 rounded-md border border-dashed border-gray-200 flex items-center justify-center text-[#05144b] hover:border-[#05144b] transition-all cursor-pointer overflow-hidden bg-white">
                                            <i className="fa-solid fa-plus text-xs"></i>
                                            <input type="file" multiple className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleFileUpload(e, 'gallery')} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-10 border-t border-gray-100">
                            <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-6">
                                <div>
                                    <h6 className="text-[15px] font-bold text-black flex items-center gap-2 mb-1">
                                        <div className="w-2 h-4 bg-[#05144b] rounded-full"></div>
                                        Geographical Proximity Mapping
                                    </h6>
                                    <p className="text-[13px] text-black font-medium ml-3.5">Organize landmarks by infrastructure categories</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={handleLandmarkSampleDownload}
                                        className="h-12 px-5 bg-white border-2 border-[#05144b] hover:border-yellow-400 text-[#05144b] text-[12px] font-bold rounded-md hover:bg-blue-50 transition-all flex items-center gap-2 shadow-md active:scale-95"
                                        title="Download Landmark Import Template"
                                    >
                                        <i className="fa-solid fa-cloud-download text-yellow-500"></i>
                                        Template
                                    </button>

                                    <div className="relative">
                                        <button
                                            type="button"
                                            className="h-12 px-5 bg-white border-2 border-[#05144b] hover:border-yellow-400 text-[#05144b] text-[12px] font-bold rounded-md hover:bg-blue-50 transition-all flex items-center gap-2 shadow-md active:scale-95"
                                        >
                                            <i className="fa-solid fa-file-import text-yellow-500"></i>
                                            Bulk Import
                                        </button>
                                        <input
                                            type="file"
                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                            accept=".xlsx, .xls"
                                            onChange={handleLandmarkImport}
                                        />
                                    </div>

                                    <div className="relative group">
                                        <select 
                                            className="min-w-[240px] h-12 px-6 bg-white border-2 border-[#05144b] text-[#05144b] text-[12px] font-bold rounded-md hover:bg-blue-50 transition-all outline-none appearance-none cursor-pointer shadow-md"
                                            onChange={(e) => {
                                                const catId = e.target.value;
                                                if (!catId) return;
                                                // Add first landmark for this category to initialize group
                                                setFormNearby([...formNearby, { name: '', km: '', image: '', category: catId }]);
                                                e.target.value = ''; // Reset
                                            }}
                                        >
                                            <option value="" className="bg-white text-black font-bold">+ Initialize Category Group</option>
                                            {categories.map(cat => (
                                                <option key={cat._id} value={cat._id} className="bg-white text-black font-medium">{cat.name}</option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                                            <i className="fa-solid fa-plus text-[10px] text-[#05144b]"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                                {categories.map(cat => {
                                    const landmarksInCategory = formNearby.filter(nb => {
                                        const nbCatId = (nb.category && typeof nb.category === 'object') ? (nb.category as any)._id : nb.category;
                                        return nbCatId === cat._id;
                                    });
                                    
                                    if (landmarksInCategory.length === 0) return null;

                                    return (
                                        <div key={cat._id} className="bg-gray-50/50 rounded-2xl border border-gray-100 overflow-hidden shadow-sm animate-in fade-in slide-in-from-top-4 duration-500">
                                            <div className="p-3 border-b border-gray-100 bg-white/80 flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-lg bg-[#05144b] text-white flex items-center justify-center shadow-md">
                                                        <i className="fa-solid fa-map-pin text-sm"></i>
                                                    </div>
                                                    <div>
                                                        <h4 className="text-[16px] font-bold text-black leading-tight">{cat.name}</h4>
                                                        <p className="text-[13px] text-black font-medium mt-0.5">{landmarksInCategory.length} Registered Points</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button 
                                                        type="button"
                                                        className="px-3 py-1.5 bg-white border-2 border-[#05144b] text-[11px] font-bold text-[#05144b] rounded-md hover:bg-blue-50 transition-all shadow-sm active:scale-95 flex items-center gap-1.5 whitespace-nowrap"
                                                        onClick={() => setFormNearby([...formNearby, { name: '', km: '', image: '', category: cat._id }])}
                                                    >
                                                        <i className="fa-solid fa-plus text-[8px]"></i> Add Landmark
                                                    </button>
                                                    <button 
                                                        type="button"
                                                        className="w-9 h-9 bg-red-50 text-red-500 rounded-md hover:bg-red-500 hover:text-white transition-all flex items-center justify-center border border-red-100 shadow-sm active:scale-90"
                                                        title={`Clear all ${cat.name}`}
                                                        onClick={() => setFormNearby(formNearby.filter(nb => {
                                                            const nbCatId = (nb.category && typeof nb.category === 'object') ? (nb.category as any)._id : nb.category;
                                                            return nbCatId !== cat._id;
                                                        }))}
                                                    >
                                                        <i className="fa-solid fa-trash-can text-xs"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead>
                                                        <tr className="border-b border-gray-200">
                                                            <th className="pb-2 text-left text-[12px] font-bold text-black pl-2">#</th>
                                                            <th className="pb-2 text-left text-[12px] font-bold text-black">Landmark / Institution Name</th>
                                                            <th className="pb-2 text-left text-[12px] font-bold text-black w-[150px]">Distance (KM)</th>
                                                            <th className="pb-2 text-center text-[12px] font-bold text-black w-[80px]">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-blue-900/5">
                                                        {landmarksInCategory.map((nb, localIdx) => {
                                                            const globalIdx = formNearby.findIndex(item => item === nb);
                                                            return (
                                                                <tr key={localIdx} className="group hover:bg-gray-100 transition-all">
                                                                    <td className="py-1 text-[13px] font-bold text-black pl-2">{localIdx + 1}.</td>
                                                                    <td className="py-1">
                                                                        <input
                                                                            type="text"
                                                                            className="w-full bg-transparent border-none text-[15px] font-bold text-black outline-none placeholder:text-black"
                                                                            placeholder="Enter name..."
                                                                            value={nb.name}
                                                                            onChange={(e) => {
                                                                                const n = [...formNearby];
                                                                                n[globalIdx].name = e.target.value;
                                                                                setFormNearby(n);
                                                                            }}
                                                                        />
                                                                    </td>
                                                                    <td className="py-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <input
                                                                                type="text"
                                                                                className="w-full bg-transparent border-none text-[15px] font-bold text-black outline-none placeholder:text-black"
                                                                                placeholder="0.0"
                                                                                value={nb.km}
                                                                                onChange={(e) => {
                                                                                    const n = [...formNearby];
                                                                                    n[globalIdx].km = e.target.value;
                                                                                    setFormNearby(n);
                                                                                }}
                                                                            />
                                                                            <span className="text-[12px] font-bold text-black">KM</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-1 text-center">
                                                                        <div className="flex items-center justify-center gap-1 mx-auto">
                                                                            <button
                                                                                type="button"
                                                                                className="w-8 h-8 rounded-md text-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center opacity-40 group-hover:opacity-100"
                                                                                onClick={() => setFormNearby([...formNearby, { name: '', km: '', image: '', category: cat._id }])}
                                                                                title="Quick Add Landmark"
                                                                            >
                                                                                <i className="fa-solid fa-plus text-[10px]"></i>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="w-8 h-8 rounded-md text-red-400 hover:bg-red-50 hover:text-red-500 transition-all flex items-center justify-center opacity-40 group-hover:opacity-100"
                                                                                onClick={() => setFormNearby(formNearby.filter((_, i) => i !== globalIdx))}
                                                                                title="Delete Landmark"
                                                                            >
                                                                                <i className="fa-solid fa-trash-can text-[10px]"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Empty State Illustration */}
                                {formNearby.length === 0 && (
                                    <div className="py-24 border-2 border-dashed border-gray-100 rounded-3xl flex flex-col items-center justify-center bg-blue-50/20 text-center px-6">
                                        <div className="w-20 h-20 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-black/20 mb-6 shadow-xl rotate-3">
                                            <i className="fa-solid fa-map-location-dot text-3xl"></i>
                                        </div>
                                        <h5 className="text-[16px] font-bold text-black mb-2">No Geographic Nodes Active</h5>
                                        <p className="text-[13px] text-black font-medium max-w-xs leading-relaxed">
                                            Begin by selecting an infrastructure category from the dropdown above to initialize a mapping group for this sector.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Supplementary Metadata - Refactored to Checkboxes */}
                        <div className="pt-10 border-t border-gray-100">
                            <div className="flex items-center justify-between mb-8">
                                <h6 className="text-[15px] font-bold text-black flex items-center gap-2">
                                    <div className="w-2 h-4 bg-[#05144b] rounded-full"></div>
                                    Locality Highlights
                                </h6>
                                <p className="text-[13px] text-black font-medium">Select relevant attributes for this sector</p>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {globalHighlights.map((gh) => {
                                    const isSelected = formAddDetails.some(ad => ad.label === gh.name);
                                    return (
                                        <div 
                                            key={gh._id}
                                            onClick={() => {
                                                if (isSelected) {
                                                    setFormAddDetails(formAddDetails.filter(ad => ad.label !== gh.name));
                                                } else {
                                                    setFormAddDetails([...formAddDetails, { 
                                                        label: gh.name, 
                                                        value: gh.description || 'Verified Feature', 
                                                        icon: gh.icon 
                                                    }]);
                                                }
                                            }}
                                            className={`
                                                relative flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer select-none group
                                                ${isSelected 
                                                    ? 'border-[#05144b] bg-blue-50 shadow-md' 
                                                    : 'border-gray-100 bg-white hover:border-gray-200'}
                                            `}
                                        >
                                            <div className={`
                                                w-5 h-5 rounded flex items-center justify-center border-2 transition-all
                                                ${isSelected ? 'bg-[#05144b] border-[#05144b] text-white' : 'border-gray-200 group-hover:border-gray-400'}
                                            `}>
                                                {isSelected && <i className="fa-solid fa-check text-[10px]"></i>}
                                            </div>
                                            <div className="flex-grow min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <div className="w-4 h-4 shrink-0 flex items-center justify-center text-black/60">
                                                        {gh.icon && (gh.icon.startsWith('fa-') || !gh.icon.includes('/')) ? (
                                                            <i className={`fa-solid ${gh.icon} text-[10px]`}></i>
                                                        ) : (
                                                            <img src={getImageUrl(gh.icon)} className="w-full h-full object-contain" />
                                                        )}
                                                    </div>
                                                    <span className={`text-[14px] font-bold truncate ${isSelected ? 'text-black' : 'text-black/60'}`}>
                                                        {gh.name}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {globalHighlights.length === 0 && (
                                <div className="py-12 border border-dashed border-gray-100 rounded-xl flex flex-col items-center justify-center bg-blue-50/20">
                                    <p className="text-[12px] font-bold text-black uppercase tracking-widest">No global highlights configured.</p>
                                    <button 
                                        type="button"
                                        onClick={() => navigate('/admin-locality-highlights')}
                                        className="mt-4 text-[11px] font-bold text-black underline"
                                    >
                                        Configure highlights matrix
                                    </button>
                                </div>
                            )}
                        </div>


                        <div className="flex justify-end pt-10 border-t border-gray-100">
                             <button
                                type="submit"
                                className="w-full lg:w-[350px] h-13 bg-white border-2 border-[#05144b] text-[#05144b] text-[11px] font-bold rounded-md shadow-md hover:bg-blue-50 active:scale-95 transition-all flex items-center justify-center gap-4 disabled:opacity-50"
                                disabled={submitting || uploading}
                             >
                                <i className={`fa-solid ${submitting ? 'fa-spinner fa-spin' : 'fa-check-double'}`}></i>
                                {submitting ? 'Synchronizing Intelligence...' : id ? 'Authorize Update' : 'Finalize Registration'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

        </AdminLayout>
    )
}



